import { Package, ArrowUpRight, PenLine, CheckCircle2 } from 'lucide-react';
import { memo, useCallback, useMemo } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { useItemStore } from '@/store';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type Props = {
  item: IOneItemInPrepareForRequest;
  handleOpenEditDialog: (item: IOneItemInPrepareForRequest) => void;
};

// Pre-define styles as constants to avoid recreating them on every render
const CARD_STYLES = {
  container:
    'relative flex h-full w-full flex-col overflow-hidden rounded-md border shadow-sm',
  containerLight:
    'border-slate-200 bg-gradient-to-br from-white via-slate-50/90 to-white',
  containerDark:
    'border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-950/90 to-slate-900',

  clientButton:
    'relative flex h-auto w-fit items-center gap-1.5 overflow-hidden rounded-md px-2 py-1.5 text-left text-sm font-medium',
  clientButtonLight: 'bg-indigo-50/80 text-indigo-700',
  clientButtonDark: 'bg-indigo-950/50 text-indigo-100',

  itemButton:
    'relative flex h-auto w-fit items-center gap-1.5 self-end overflow-hidden rounded-md px-2 py-1 text-[11px] font-medium',
  itemButtonLight: 'bg-gray-100/80 text-gray-600',
  itemButtonDark: 'bg-gray-800/70 text-gray-400',

  priceTag:
    'ml-1.5 flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium shadow-sm',
  priceTagLight: 'border-green-200 bg-green-50 text-green-700',
  priceTagDark: 'border-green-900/50 bg-green-900/30 text-green-400',

  editIcon:
    'ml-1 h-3 w-3 shrink-0 opacity-70 transition-opacity group-hover:opacity-100',

  badge:
    'absolute top-0 right-0 flex h-3 w-3 items-center justify-center rounded-full border-2 border-white shadow-sm z-30 dark:border-gray-800',
  badgeSuccess: 'bg-green-600 text-white dark:bg-green-500 dark:text-white',
};

function ClientItemNameCol({ item, handleOpenEditDialog }: Props) {
  const { openItemDialog } = useItemStore();

  // Memoize values that are used in rendering or calculations
  const hasHealthyItem = useMemo(
    () => Boolean(item?.HealthyItemID),
    [item?.HealthyItemID],
  );

  const formattedPrice = useMemo(
    () =>
      item?.healthyItemPrice
        ? formatNumberWithCommas(item.healthyItemPrice)
        : null,
    [item?.healthyItemPrice],
  );

  // Memoize event handlers
  const handleEditClick = useCallback(() => {
    if (item) {
      handleOpenEditDialog(item);
    }
  }, [item, handleOpenEditDialog]);

  const handleHealthyItemClick = useCallback(() => {
    if (item?.HealthyItemID) {
      openItemDialog(item.HealthyItemID);
    }
  }, [item?.HealthyItemID, openItemDialog]);

  return (
    <div className="relative h-full w-full">
      {/* Green badge for isLastUpdate */}
      {item?.isLastUpdate && (
        <div className={cn(CARD_STYLES.badge, CARD_STYLES.badgeSuccess)}>
          <CheckCircle2 className="h-3 w-3 animate-pulse" />
        </div>
      )}

      <div
        className={cn(
          CARD_STYLES.container,
          'h-16 dark:border-slate-800 dark:bg-gradient-to-br dark:from-slate-900/95 dark:via-slate-950/90 dark:to-slate-900',
          'border-slate-200 bg-gradient-to-br from-white via-slate-50/90 to-white',
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Client Name */}
              <Button
                variant="ghost"
                className={cn(
                  CARD_STYLES.clientButton,
                  'dark:bg-indigo-950/50 dark:text-indigo-100',
                  'bg-indigo-50/80 text-indigo-700',
                  'group',
                )}
                onClick={handleEditClick}
              >
                <Package className="h-3.5 w-3.5 text-indigo-500/70 dark:text-indigo-100" />
                <span className="truncate font-medium">
                  {item?.ClientItemName || '-'}
                </span>
                <PenLine className={cn(CARD_STYLES.editIcon)} />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="start" className="max-w-xs">
              <div className="space-y-1 text-xs">
                <p className="font-medium">اسم الصنف العميل</p>
                <p className="text-muted-foreground">
                  {item?.ClientItemName || '-'}
                </p>
                <p className="text-xs italic text-muted-foreground">
                  انقر للتعديل
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Healthy Item Name and Price */}
        {item?.itemName && (
          <div className="flex items-center justify-between">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      CARD_STYLES.itemButton,
                      'dark:bg-gray-800/70 dark:text-gray-400',
                      'bg-gray-100/80 text-gray-600',
                      !hasHealthyItem && 'pointer-events-none opacity-70',
                    )}
                    onClick={handleHealthyItemClick}
                    disabled={!hasHealthyItem}
                  >
                    <div className="flex items-center gap-1">
                      <ArrowUpRight className="h-3 w-3 text-indigo-500/70 dark:text-indigo-100" />
                      <span className="max-w-[200px] truncate font-normal">
                        {item.itemName}
                      </span>
                    </div>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="max-w-xs">
                  <div className="space-y-1 text-xs">
                    <p className="font-medium">اسم الصنف هلثي</p>
                    <p className="text-muted-foreground">{item.itemName}</p>
                    {item.HealthyItemID && (
                      <p className="text-xs text-muted-foreground">
                        رقم الصنف: {item.HealthyItemID}
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {formattedPrice && (
              <div
                className={cn(
                  CARD_STYLES.priceTag,
                  'dark:border-green-900/50 dark:bg-green-900/30 dark:text-green-400',
                  'border-green-200 bg-green-50 text-green-700',
                )}
              >
                <span>{formattedPrice}</span>
                <span className="text-[8px] opacity-80">ج.م</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Use React.memo with custom equality function to prevent unnecessary re-renders
export default memo(ClientItemNameCol, (prevProps, nextProps) => {
  // Only re-render if any of these values change
  return (
    prevProps.item?.ClientItemName === nextProps.item?.ClientItemName &&
    prevProps.item?.itemName === nextProps.item?.itemName &&
    prevProps.item?.HealthyItemID === nextProps.item?.HealthyItemID &&
    prevProps.item?.healthyItemPrice === nextProps.item?.healthyItemPrice &&
    prevProps.item?.isLastUpdate === nextProps.item?.isLastUpdate
  );
});
